import React, { useCallback, useEffect, useState } from 'react'
import { Button, Switch } from 'antd'
import { BodyText } from 'src/branding/typography'
import styled from 'styled-components/macro'
import {
  fetchOlliePushSettings,
  postOlliePushSettings,
} from 'src/utils/growthEngineApi'

export const OLLIE_PUSH_SETTINGS_COPY = Object.freeze({
  title: 'Phone alerts from Ollie',
  body: 'At most one a day, only when something urgent needs you, like a temperature out of range. Goes to every phone signed in to this venue.',
  adminOnly: 'Only a venue admin can change this.',
  loadFailed: 'Couldn’t load Ollie alerts.',
  retry: 'Try again',
  saveFailed: 'Couldn’t save that. Try again.',
})

/**
 * Venue on/off for Ollie phone alerts (step 53). Push tokens are
 * venue-level, so this is one switch for the venue; only venue admins can
 * flip it, everyone else sees where it stands.
 */
const OlliePushSettings = ({ venueId }) => {
  const [loadState, setLoadState] = useState('idle')
  const [settings, setSettings] = useState(null)
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState(false)

  const load = useCallback(async () => {
    if (!venueId) return
    setLoadState('loading')
    try {
      setSettings(await fetchOlliePushSettings(venueId))
      setLoadState('ready')
    } catch (err) {
      console.warn('Ollie alerts failed to load', err)
      setLoadState('error')
    }
  }, [venueId])

  useEffect(() => {
    load()
  }, [load])

  const toggle = async checked => {
    if (!venueId || busy || !settings?.canManage) return
    const previous = settings
    setSettings({ ...settings, enabled: Boolean(checked) })
    setSaveError(false)
    setBusy(true)
    try {
      setSettings(await postOlliePushSettings(venueId, Boolean(checked)))
    } catch (err) {
      console.warn('Ollie alerts failed to save', err)
      setSettings(previous)
      setSaveError(true)
    }
    setBusy(false)
  }

  if (!venueId) return null

  if (loadState === 'error') {
    return (
      <Row data-testid='ollie-push-settings'>
        <BodyText>
          {OLLIE_PUSH_SETTINGS_COPY.loadFailed}{' '}
          <Button type='link' onClick={load} data-testid='ollie-push-retry'>
            {OLLIE_PUSH_SETTINGS_COPY.retry}
          </Button>
        </BodyText>
      </Row>
    )
  }

  if (loadState !== 'ready' || !settings) return null

  return (
    <Row data-testid='ollie-push-settings'>
      <Copy>
        <Title>{OLLIE_PUSH_SETTINGS_COPY.title}</Title>
        <Body>{OLLIE_PUSH_SETTINGS_COPY.body}</Body>
        {!settings.canManage ? (
          <Body data-testid='ollie-push-admin-only'>
            {OLLIE_PUSH_SETTINGS_COPY.adminOnly}
          </Body>
        ) : null}
        {saveError ? (
          <ErrorText role='alert'>
            {OLLIE_PUSH_SETTINGS_COPY.saveFailed}
          </ErrorText>
        ) : null}
      </Copy>
      <Switch
        checked={settings.enabled !== false}
        onChange={toggle}
        disabled={busy || !settings.canManage}
        aria-label={OLLIE_PUSH_SETTINGS_COPY.title}
        data-testid='ollie-push-toggle'
      />
    </Row>
  )
}

const Row = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme?.spacing?.md || '20px'};
`

const Copy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Title = styled.h4`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Body = styled(BodyText)`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`

const ErrorText = styled(Body)`
  color: ${({ theme }) => theme?.colours?.chilliRed || '#d4380d'};
`

export default OlliePushSettings
